import { useCallback, useEffect, useRef, useState } from 'react';
import type { ChangeEvent, KeyboardEvent, ReactElement } from 'react';
import { buildComponentMeta, useComponentMeta } from '../../ui';
import { ImageCropDialog } from '../../ui/ImageCropDialog';
import { emptyProfile, loadActiveProfile, saveProfile } from './profile';
import type { PersonalProfile, ProfileDraft } from './profile';
import { profileMeta } from './profileMeta';
import styles from './ProfileEditor.module.css';

function useProfileMeta<T extends HTMLElement>(id: string) {
  const meta = buildComponentMeta(profileMeta(id));
  const ref = useComponentMeta<T>(meta);
  return { ref, 'data-ui-id': meta.id, 'data-ui-role': meta.role };
}

function ProfileField({
  field,
  label,
  value,
  disabled,
  onChange,
}: {
  field: 'nickname' | 'birthday' | 'interests' | 'bio';
  label: string;
  value: string;
  disabled: boolean;
  onChange: (value: string) => void;
}): ReactElement {
  const labelMeta = useProfileMeta<HTMLSpanElement>(`${field}.label`);
  const meta = useProfileMeta<HTMLInputElement & HTMLTextAreaElement>(field);
  const props = {
    ...meta,
    id: `profile-${field}`,
    className: styles.input,
    value,
    disabled,
    onChange: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      onChange(event.currentTarget.value),
  };
  return (
    <label className={styles.field} htmlFor={props.id}>
      <span {...labelMeta}>{label}</span>
      {field === 'bio' ? (
        <textarea {...props} maxLength={2000} rows={3} />
      ) : (
        <input
          {...props}
          type={field === 'birthday' ? 'date' : 'text'}
          maxLength={field === 'nickname' ? 80 : field === 'interests' ? 500 : 10}
        />
      )}
    </label>
  );
}

function Feedback({ kind, text }: { kind: 'error' | 'status'; text: string }): ReactElement {
  const meta = useProfileMeta<HTMLParagraphElement>(kind);
  return (
    <p
      {...meta}
      className={`${styles.feedback} ${styles[kind]}`}
      role={kind === 'error' ? 'alert' : 'status'}
    >
      {text}
    </p>
  );
}

export interface ProfileEditorProps {
  userId: string;
  onClose: () => void;
  onSaved?: (profile: PersonalProfile) => void;
}

export function ProfileEditor({ userId, onClose, onSaved }: ProfileEditorProps): ReactElement {
  const [draft, setDraft] = useState<ProfileDraft>(() => emptyProfile(userId));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [cropFile, setCropFile] = useState<File | null>(null);
  const mounted = useRef(false);
  const running = useRef(false);
  const epoch = useRef(0);
  const invalidate = useCallback(() => {
    ++epoch.current;
  }, []);
  const backdrop = useProfileMeta<HTMLDivElement>('backdrop');
  const dialog = useProfileMeta<HTMLDivElement>('dialog');
  const title = useProfileMeta<HTMLHeadingElement>('title');
  const hint = useProfileMeta<HTMLParagraphElement>('hint');
  const consent = useProfileMeta<HTMLInputElement>('consent');
  const cancel = useProfileMeta<HTMLButtonElement>('cancel');
  const save = useProfileMeta<HTMLButtonElement>('save');
  const avatarInput = useProfileMeta<HTMLInputElement>('avatar');
  const avatarPreview = useProfileMeta<HTMLDivElement>('avatar.preview');

  useEffect(() => {
    mounted.current = true;
    invalidate();
    running.current = false;
    setSaving(false);
    let active = true;
    const previousFocus = document.activeElement;
    dialog.ref.current?.focus();
    setDraft(emptyProfile(userId));
    setLoading(true);
    setError(null);
    setStatus(null);
    setCropFile(null);
    void loadActiveProfile()
      .then((profile) => {
        if (!active) return;
        if (profile !== null && profile.userId !== userId)
          throw new Error('登录账户已变化，请重新打开个人资料');
        setDraft(profile ?? emptyProfile(userId));
      })
      .catch((loadError: unknown) => {
        if (active) setError(loadError instanceof Error ? loadError.message : '读取个人资料失败');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
      mounted.current = false;
      invalidate();
      if (previousFocus instanceof HTMLElement) previousFocus.focus();
    };
  }, [userId, dialog.ref, invalidate]);

  function change(field: keyof ProfileDraft, value: string | boolean): void {
    setDraft((current) => ({ ...current, [field]: value }));
    setStatus(null);
  }

  async function submit(): Promise<void> {
    if (loading || running.current) return;
    running.current = true;
    const version = epoch.current;
    setSaving(true);
    setError(null);
    setStatus(null);
    try {
      const profile = await saveProfile(userId, draft);
      if (!mounted.current || epoch.current !== version) return;
      setDraft(profile);
      setStatus('个人资料已保存到本机');
      onSaved?.(profile);
    } catch (saveError) {
      if (mounted.current && epoch.current === version)
        setError(`保存失败：${saveError instanceof Error ? saveError.message : '请稍后重试'}`);
    } finally {
      if (epoch.current === version) {
        running.current = false;
        if (mounted.current) setSaving(false);
      }
    }
  }

  function keyDown(event: KeyboardEvent<HTMLDivElement>): void {
    if (cropFile) return;
    if (event.key === 'Escape' && !saving) {
      event.preventDefault();
      onClose();
    }
    if (event.key !== 'Tab') return;
    const controls = dialog.ref.current?.querySelectorAll<HTMLElement>(
      'input:not(:disabled), textarea:not(:disabled), button:not(:disabled)',
    );
    if (!controls?.length) {
      event.preventDefault();
      return;
    }
    const first = controls[0]!;
    const last = controls[controls.length - 1]!;
    if (
      event.shiftKey &&
      (document.activeElement === first || document.activeElement === dialog.ref.current)
    ) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  return (
    <>
      <div {...backdrop} className={styles.backdrop} aria-hidden="true" />
      <div
        {...dialog}
        className={styles.dialog}
        data-skin-surface="overlay"
        role="dialog"
        aria-modal="true"
        aria-labelledby="profile-title"
        tabIndex={-1}
        onKeyDown={keyDown}
      >
        <h2 {...title} className={styles.title} id="profile-title">
          个人资料
        </h2>
        <p {...hint} className={styles.hint}>
          资料保存在本机，仅属于当前账户。开启允许读取后，资料将随聊天发送给所用模型。
        </p>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void submit();
          }}
        >
          <div className={styles.avatarField}>
            <div {...avatarPreview} className={styles.avatarPreview} aria-label="个人头像预览" role="img">
              {draft.avatar ? <img src={draft.avatar} alt="" /> : '我'}
            </div>
            <label className={styles.avatarUpload}>头像设置
              <input {...avatarInput} type="file" accept="image/png,image/jpeg,image/webp,image/gif" aria-label="选择个人头像"
                disabled={loading || saving} onChange={event => {
                  const file = event.currentTarget.files?.[0];
                  event.currentTarget.value = '';
                  if (file) { setCropFile(file); setStatus(null); }
                }} />
            </label>
            {draft.avatar ? <button className={styles.avatarRemove} type="button" disabled={saving} onClick={() => change('avatar', '')}>移除头像</button> : null}
          </div>
          {(['nickname', 'birthday', 'interests', 'bio'] as const).map((field, index) => (
            <ProfileField
              key={field}
              field={field}
              label={['昵称', '生日', '兴趣爱好', '个人介绍'][index]!}
              value={draft[field]}
              disabled={loading || saving}
              onChange={(value) => change(field, value)}
            />
          ))}
          <label className={styles.consent}>
            <input
              {...consent}
              type="checkbox"
              role="switch"
              aria-label="允许智能体读取个人资料"
              checked={draft.allowAgentRead}
              disabled={loading || saving}
              onChange={(event) => change('allowAgentRead', event.currentTarget.checked)}
            />
            允许智能体读取个人资料
          </label>
          {error ? (
            <Feedback kind="error" text={error} />
          ) : (
            <Feedback
              kind="status"
              text={
                loading
                  ? '正在读取个人资料…'
                  : (status ?? '关闭读取授权后，后续聊天不再附带个人资料。')
              }
            />
          )}
          <div className={styles.actions}>
            <button
              {...cancel}
              className={styles.button}
              type="button"
              disabled={saving}
              onClick={onClose}
            >
              关闭
            </button>
            <button
              {...save}
              className={`${styles.button} ${styles.primary}`}
              type="submit"
              disabled={loading || saving}
            >
              {saving ? '保存中…' : '保存个人资料'}
            </button>
          </div>
        </form>
      </div>
      {cropFile ? <ImageCropDialog file={cropFile} kind="avatar" onCancel={() => setCropFile(null)} onConfirm={dataUrl => {
        change('avatar', dataUrl); setCropFile(null);
      }} /> : null}
    </>
  );
}
